import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Alert, Box, Button, Checkbox, FormControlLabel, TextField, Chip, Dialog, DialogContent, DialogTitle, LinearProgress, Paper, Stack, Typography, Grid, IconButton, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { PageHeader } from './patterns';
import { OneTimeDownloadButton, OneTimeDocumentCard } from './OneTimeStagePatterns';
import StepProgressSpinner from './StepProgressSpinner';
import { CrrState, CrrEvidence, CrrProfile, getDcmaCrrProfile, saveDcmaCrrProfile, getDcmaCrrReview, uploadDcmaCrrSources, startDcmaCrrRun, cancelDcmaCrrRun, downloadDcmaCrrSource, downloadDcmaCrrWorkbook, deleteDcmaCrrSource, dcmaCrrErrorMessage } from '../api/dcmaCrrFunctions';
import { CloudUpload as CloudUploadIcon, CheckCircle as CheckCircleIcon, Delete as DeleteIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useUploadGuard } from '../hooks/useUploadGuard';
import { GLOBAL_NAVBAR_HEIGHT, GLOBAL_NAVBAR_Z_INDEX } from './globalNavbarLayout';
import { MOBILE_DENSITY } from '../theme/mobileDensity';
const MAX_BYTES = 20 * 1024 * 1024;
const STEPS = [{
  step_id: 'reading',
  label: 'Reading pages'
}, {
  step_id: 'checking',
  label: 'Checking contract evidence'
}, {
  step_id: 'exporting',
  label: 'Preparing checklist'
}];
function usableClauseEvidence(item: CrrEvidence): boolean {
  return Number.isInteger(item.page) && item.page >= 1 &&
    typeof item.source_id === 'string' && item.source_id.trim().length > 0 &&
    typeof item.excerpt === 'string' && item.excerpt.trim().length > 0;
}

function groupPageEvidence(items: CrrEvidence[]): CrrEvidence[] {
  const groups = new Map<string, { first: CrrEvidence; excerpts: Set<string> }>();
  for (const item of items) {
    const key = JSON.stringify([item.source_id, item.page]);
    const group = groups.get(key) || { first: item, excerpts: new Set<string>() };
    if (item.excerpt?.trim()) group.excerpts.add(item.excerpt.trim());
    groups.set(key, group);
  }
  return Array.from(groups.values()).map(group => ({ ...group.first, excerpt: Array.from(group.excerpts).join('\n\n') }));
}

// The One-Time Services upload tile: one dashed panel, in-panel file feedback,
// and acknowledgement-based success styling. CRR keeps its explicit check action.
function CrrUploadTile({ title, label, inputLabel, selected, saved, onFiles, onRemove, onDeleteSaved, uploading, disabled, multiple = false, required = false, testId }: {
  title: string; label: string; inputLabel: string;
  selected: File[]; saved: { source_id: string; filename: string; size?: number }[];
  onFiles: (files: File[]) => void; onRemove: (index: number) => void; onDeleteSaved: (source: string) => void;
  uploading: boolean; disabled: boolean; multiple?: boolean; required?: boolean; testId: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const requiredDescriptionId = useId();
  const [dragging, setDragging] = useState(false);
  const { disabledRef, openPicker } = useUploadGuard(disabled);
  useEffect(() => { if (disabled) setDragging(false); }, [disabled]);
  const choose = () => openPicker(input.current);
  const accept = (files: FileList | null) => {
    if (!disabledRef.current && files?.length) onFiles(Array.from(files));
  };
  const formatSize = (bytes: number) => bytes < 1024 ? `${bytes} Bytes` : bytes < 1024 * 1024 ? `${parseFloat((bytes / 1024).toFixed(2))} KB` : `${parseFloat((bytes / (1024 * 1024)).toFixed(2))} MB`;
  return <OneTimeDocumentCard data-testid={testId} role="button" aria-label={label}
    tabIndex={disabled ? -1 : 0} disabled={disabled} aria-describedby={required ? requiredDescriptionId : undefined}
    onClick={choose}
    onKeyDown={event => {
      if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) return;
      event.preventDefault(); choose();
    }}
    onDragOver={event => { event.preventDefault(); if (!disabledRef.current) setDragging(true); }}
    onDragLeave={() => setDragging(false)}
    onDrop={event => { event.preventDefault(); event.stopPropagation(); setDragging(false); accept(event.dataTransfer.files); }}
    sx={{ p: 1.5, height: '100%', minHeight: 132, minWidth: 0, textAlign: 'center',
      borderColor: dragging && !disabled ? 'primary.main' : saved.length && !selected.length ? 'success.main' : 'grey.300',
      backgroundColor: dragging && !disabled ? 'action.hover' : saved.length && !selected.length ? 'success.light' : 'background.paper',
      transition: 'all 0.2s ease-in-out', '&:hover': disabled ? undefined : { borderColor: 'primary.main' } }}>
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}><Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1, textAlign: 'left' }}>{title}{required && <Typography component="span" color="error.main" aria-hidden="true" sx={{ ml: 0.5 }}>*</Typography>}</Typography>
      {!multiple && !selected.length && saved.length === 1 && <IconButton size="small" color="error" disabled={disabled} aria-label={`Remove uploaded ${saved[0].filename}`} onClick={event => { event.stopPropagation(); onDeleteSaved(saved[0].source_id); }}><DeleteIcon fontSize="small" /></IconButton>}
    </Box>
    {required && <Box component="span" id={requiredDescriptionId} sx={{ position: 'absolute', width: '1px', height: '1px', p: 0, m: '-1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 }}>Contract PDF is required.</Box>}
    <input ref={input} type="file" accept=".pdf,application/pdf" multiple={multiple} disabled={disabled} required={required} aria-label={inputLabel}
      onClick={event => event.stopPropagation()} onChange={event => { accept(event.target.files); event.target.value = ''; }} style={{ display: 'none' }} />
    <Box sx={{ flexGrow: 1, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
      {uploading ? <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={20} /><Typography variant="body2" role="status">Uploading...</Typography></Stack> : !selected.length && !saved.length ? <CloudUploadIcon sx={{ fontSize: 32, color: 'text.secondary' }} /> : null}
      {!selected.length && !saved.length && !uploading && <Typography variant="body2" color="text.secondary">Drop {multiple ? 'PDFs' : 'a PDF'} here or click to browse</Typography>}
      {selected.map((file, i) => <Box key={`${file.name}:${i}`} sx={{ width: '100%', p: 0.75, borderRadius: 1, backgroundColor: 'background.paper' }}>
        <Stack direction="row" spacing={1} alignItems="center"><Chip size="small" label={uploading ? 'Uploading' : 'Selected'} /><Typography variant="body2" fontWeight="medium" sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{file.name}</Typography>
          <IconButton size="small" color="error" disabled={disabled} aria-label={multiple ? `Remove selected ${file.name}` : 'Remove selected contract'} onClick={event => { event.stopPropagation(); onRemove(i); }}><DeleteIcon fontSize="small" /></IconButton>
        </Stack><Typography variant="caption" color="text.secondary">{formatSize(file.size)}</Typography>
      </Box>)}
      {saved.map(file => <Box key={file.filename} sx={{ width: '100%', p: 0.75, borderRadius: 1, backgroundColor: 'background.paper' }}>
        <Stack direction="row" spacing={1} alignItems="center"><CheckCircleIcon color="success" sx={{ fontSize: 20 }} /><Typography variant="body2" fontWeight="medium" sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{file.filename}</Typography><Chip size="small" color="success" label="Saved" />{multiple && <IconButton size="small" color="error" disabled={disabled} aria-label={`Remove uploaded ${file.filename}`} onClick={event => { event.stopPropagation(); onDeleteSaved(file.source_id); }}><DeleteIcon fontSize="small" /></IconButton>}</Stack>
        {!!file.size && <Typography variant="caption" color="text.secondary">{formatSize(file.size)}</Typography>}
      </Box>)}
    </Box>
  </OneTimeDocumentCard>;
}

export default function DcmaCrrWorkflow({
  flowId,
  workflowName,
  readOnly = false
}: {
  flowId: string;
  workflowName?: string;
  readOnly?: boolean;
}) {
  const [state, setState] = useState<CrrState | null>(null),
    [contract, setContract] = useState<File | null>(null),
    [support, setSupport] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [expectedAdmin, setExpectedAdmin] = useState('');
  const [profile, setProfile] = useState<CrrProfile | null>(null);
  const [useProfileDefault, setUseProfileDefault] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileDraft, setProfileDraft] = useState('');
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileError, setProfileError] = useState('');
  const contextTouched = useRef(false);
  const defaultApplied = useRef(false);
  const contextRevision = useRef<number | null>(null);
  const [useSavedWork, setUseSavedWork] = useState(false);
  const defaultedSavedJob = useRef<string | null>(null);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [sourceView, setSourceView] = useState<{
      url: string;
      page: number;
      title: string;
      filename: string;
      excerpt: string;
    } | null>(null);
  const loadSequence = useRef(0);
  const epoch = useRef(0),
    permission = useRef(readOnly);
  permission.current = readOnly;
  const active = state?.run?.status === 'queued' || state?.run?.status === 'running';
  const locked = readOnly || busy || active;
  const savedJob = state?.run?.status === 'completed' ? state.run.job_id : null;
  const expectedAdminValue = expectedAdmin.trim().toUpperCase();
  const selectedDefault: readonly [useDefault?: true, revision?: number] = useProfileDefault && profile ? [true, profile.revision] : [];
  const contextChanged = expectedAdminValue !== (state?.expected_admin_dodaac || '') || (useProfileDefault && (state?.expected_admin_origin !== 'user_profile' || profile?.revision !== state?.expected_admin_profile_revision));
  const pendingInputs = !!contract || support.length > 0;
  const hasSavedWork = !!savedJob && !contract && support.length === 0 && !contextChanged;
  const savedPrimary = state?.sources.find(source => source.role === 'contract');
  // A current export belongs to the displayed inputs/run, not a later snapshot.
  const currentExportIdentity = useRef('');
  currentExportIdentity.current = JSON.stringify([flowId, state?.revision, state?.run?.job_id, state?.run?.generation, state?.sources.map(source => [source.source_id, source.sha256]), expectedAdminValue, contextChanged, contract && [contract.name, contract.size, contract.lastModified], support.map(file => [file.name, file.size, file.lastModified])]);
  const load = useCallback(async () => {
    const id = epoch.current;
    const sequence = ++loadSequence.current;
    try {
      const next = await getDcmaCrrReview(flowId);
      if (id === epoch.current && sequence === loadSequence.current) setState(current => current && (current.revision > next.revision || (current.revision === next.revision && current.run && next.run && current.run.job_id === next.run.job_id && current.run.generation > next.run.generation)) ? current : next);
    } catch (e) {
      if (id === epoch.current) setError(dcmaCrrErrorMessage(e));
    }
  }, [flowId]);
  useEffect(() => {
    epoch.current += 1;
    setState(null);
    setExpectedAdmin('');
    setUseProfileDefault(false);
    contextTouched.current = false;
    defaultApplied.current = false;
    setProfileOpen(false);
    contextRevision.current = null;
    setBusy(false);
    setUploading(false);
    setUseSavedWork(false);
    defaultedSavedJob.current = null;
    setContract(null);
    setSupport([]);
    setSourceView(null);
    setError('');
    void load();
    return () => {
      epoch.current += 1;
    };
  }, [load]);
  useEffect(() => {
    if (state && contextRevision.current !== state.revision) {
      contextRevision.current = state.revision;
      setExpectedAdmin(state.expected_admin_dodaac || '');
      setUseProfileDefault(false);
    }
  }, [state]);
  useEffect(() => {
    let current = true;
    void getDcmaCrrProfile().then(next => { if (current) { setProfile(next); setProfileError(''); } })
      .catch(e => { if (current) setProfileError(dcmaCrrErrorMessage(e)); });
    return () => { current = false; };
  }, []);
  useEffect(() => {
    if (profile && state && state.revision === 0 && state.sources.length === 0 && !state.run && !contextTouched.current && !defaultApplied.current) {
      defaultApplied.current = true;
      if (profile.admin_dodaac) { setExpectedAdmin(profile.admin_dodaac); setUseProfileDefault(true); }
    }
  }, [profile, state]);
  useEffect(() => {
    if (useProfileDefault && profile) setExpectedAdmin(profile.admin_dodaac || '');
  }, [profile, useProfileDefault]);
  const saveProfile = async () => {
    if (!profile || readOnly || profileBusy) return;
    const value = profileDraft.trim().toUpperCase();
    if (value && !/^[A-Z0-9]{6}$/.test(value)) { setProfileError('Enter a six-character administering DoDAAC or leave it blank.'); return; }
    const id = epoch.current;
    setProfileBusy(true); setProfileError('');
    try {
      const next = await saveDcmaCrrProfile(value, profile.revision);
      // The profile is caller-scoped and persists even if the workflow changes
      // while the request is in flight. Only the visible flow choice is scoped
      // to the workflow epoch.
      setProfile(next);
      if (id === epoch.current) {
        // Saving a future default does not change the visible per-contract choice.
        setUseProfileDefault(false); contextTouched.current = true; setProfileOpen(false);
      }
    } catch (e) { if (id === epoch.current) setProfileError(dcmaCrrErrorMessage(e)); }
    finally { setProfileBusy(false); }
  };
  useEffect(() => {
    if (!savedJob) { setUseSavedWork(false); return; }
    if (hasSavedWork && defaultedSavedJob.current !== savedJob) {
      defaultedSavedJob.current = savedJob;
      setUseSavedWork(true);
    }
  }, [savedJob, hasSavedWork]);
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => void load(), 1500);
    return () => window.clearInterval(timer);
  }, [active, load]);
  useEffect(() => () => {
    if (sourceView) URL.revokeObjectURL(sourceView.url);
  }, [sourceView]);
  const action = async (fn: () => Promise<void>) => {
    const id = epoch.current;
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (e) {
      if (id === epoch.current) setError(dcmaCrrErrorMessage(e));
    } finally {
      if (id === epoch.current) setBusy(false);
    }
  };
  const selectContract = (files: File[]) => {
    if (permission.current || busy || active) return;
    if (files.length !== 1) {
      setError('Choose exactly one contract PDF.');
      return;
    }
    const file = files[0];
    if (!/\.pdf$/i.test(file.name) || (file.type && file.type !== 'application/pdf')) {
      setError('Choose a PDF contract.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('Each file must be 20 MiB or smaller.');
      return;
    }
    setContract(file);
    setUseSavedWork(false);
    setError('');
  };
  const selectSupport = (files: File[]) => {
    if (permission.current || busy || active) return;
    if (files.some(file => !/\.pdf$/i.test(file.name) || (file.type && file.type !== 'application/pdf') || file.size > MAX_BYTES) || files.length + support.length > 10) {
      setError('Choose up to 10 supporting PDF files, each 20 MiB or smaller.');
      return;
    }
    setSupport(current => [...current, ...files]);
    setUseSavedWork(false);
    setError('');
  };
  const removeSavedSource = (source: string) => {
    if (!state || permission.current || locked) return;
    const revision = state.revision;
    const id = ++epoch.current;
    void action(async () => {
      const next = await deleteDcmaCrrSource(flowId, source, revision);
      if (id === epoch.current) {
        setState(next); setUseSavedWork(false); setSourceView(null);
      }
    });
  };
  const validAdminContext = () => {
    if (expectedAdminValue && !/^[A-Z0-9]{6}$/.test(expectedAdminValue)) {
      setError('Enter a six-character administering DoDAAC or leave it blank.');
      return false;
    }
    return true;
  };
  const submit = () => {
    if (!state || permission.current || locked || useSavedWork || (!contract && !savedPrimary) || !validAdminContext()) return;
    const bytes = (contract?.size || 0) + support.reduce((total, file) => total + file.size, 0);
    if (bytes > 100 * 1024 * 1024) {
      setError('The combined files must be 100 MiB or smaller.');
      return;
    }
    const id = ++epoch.current;
    const sendingFiles = !!contract || support.length > 0;
    void action(async () => {
      if (sendingFiles) setUploading(true);
      try {
        const next = sendingFiles
          ? await uploadDcmaCrrSources(flowId, contract, support, state.revision, expectedAdminValue, ...selectedDefault)
          : await startDcmaCrrRun(flowId, state.revision, expectedAdminValue, ...selectedDefault);
        if (id === epoch.current) {
          setState(next); setContract(null); setSupport([]); setSourceView(null);
        }
      } finally {
        if (id === epoch.current) setUploading(false);
      }
    });
  };
  const download = (job?: string) => {
    const id = epoch.current;
    const identity = currentExportIdentity.current;
    const targetJob = job || state?.run?.job_id;
    if (!targetJob) return;
    void action(async () => {
      const blob = await downloadDcmaCrrWorkbook(flowId, targetJob);
      if (id !== epoch.current || (!job && identity !== currentExportIdentity.current)) return;
      const url = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = `crr-checklist${job ? `-${job}` : ''}.xlsm`;
        a.click();
      } finally {
        URL.revokeObjectURL(url);
      }
    });
  };
  const evidence = (items: CrrEvidence[]) => {
    const references = groupPageEvidence(items);
    const multipleSources = new Set(references.map(item => item.source_id)).size > 1;
    return references.map(item => <Button key={`${item.source_id}:${item.page}`} size="small" sx={{ overflowWrap: 'anywhere', whiteSpace: 'normal', textAlign: 'left' }} onClick={() => {
      const id = epoch.current;
      void action(async () => {
        const blob = await downloadDcmaCrrSource(flowId, item.source_id);
        if (id === epoch.current) setSourceView({
          url: URL.createObjectURL(blob), page: item.page, excerpt: item.excerpt,
          filename: state?.sources.find(source => source.source_id === item.source_id)?.filename || 'Source',
          title: `${state?.sources.find(source => source.source_id === item.source_id)?.filename || 'Source'} — page ${item.page}`
        });
      });
    }}>{multipleSources ? `${state?.sources.find(source => source.source_id === item.source_id)?.filename || 'Source'} · ` : ''}Page {item.page}</Button>);
  };
  const review = contextChanged || pendingInputs ? undefined : state?.run?.review;
  const locatedClauses = (review?.clause_results || []).filter(row => row.present === true && row.evidence.some(usableClauseEvidence));
  const mappingKind = (row: typeof locatedClauses[number]) => row.mapping_kind || ({
    'SPC-PVN-CD': 'spc', 'R9 Codes': 'r9', 'Other KCRs': 'other_kcr'
  } as const)[row.sheet as 'SPC-PVN-CD' | 'R9 Codes' | 'Other KCRs'];
  const tableRows = (kind: 'spc' | 'r9' | 'other_kcr') => {
    const rows = new Map<string, typeof locatedClauses>();
    const codes = new Map<string, number>();
    for (const row of locatedClauses.filter(item => mappingKind(item) === kind)) {
      const key = JSON.stringify([row.target, row.code || '']);
      rows.set(key, [...(rows.get(key) || []), row]);
      const code = row.code || '';
      if (!codes.has(code)) codes.set(code, codes.size);
    }
    const grouped = Array.from(rows.values());
    if (kind !== 'other_kcr') grouped.sort((a, b) => codes.get(a[0].code || '')! - codes.get(b[0].code || '')!);
    return grouped.map(contributors => ({
      ...contributors[0],
      evidence: contributors.flatMap(row => row.evidence),
      description: Array.from(new Set(contributors.map(row => row.description).filter(Boolean))).join('\n'),
      mapping_provenance: Array.from(new Set(contributors.map(row => row.mapping_provenance).filter(Boolean))).join(' · '),
      kcr_labels: Array.from(new Set(contributors.map(row => row.kcr_association === 'known' ? 'Yes' : row.kcr_association === 'none' ? 'No' : 'Not marked'))),
    }));
  };
  const clauseTable = (kind: 'spc' | 'r9' | 'other_kcr', title: string) => <Box component="section" aria-label={title}>
    <Typography component="h3" variant="subtitle1" fontWeight={600} sx={{ px: 2, py: 1 }}>{title}</Typography>
    <TableContainer data-crr-table-scroll="true" tabIndex={0} sx={{ overflowX: 'auto' }}><Table size="small" aria-label={title} sx={{ minWidth: 640, tableLayout: 'fixed' }}>
      <colgroup>
        <col style={{ width: '25%' }} />
        {kind !== 'other_kcr' && <><col style={{ width: '16%' }} /><col style={{ width: '14%' }} /></>}
        <col style={{ width: kind === 'other_kcr' ? '75%' : '45%' }} />
      </colgroup>
      <TableHead><TableRow><TableCell>Clause</TableCell>{kind !== 'other_kcr' && <><TableCell>{kind === 'spc' ? 'SPC-PVN-CD' : 'R9 remark'}</TableCell><TableCell>KCR</TableCell></>}<TableCell>Description</TableCell></TableRow></TableHead>
      <TableBody>{tableRows(kind).map(row => <TableRow key={`${row.target}:${row.code || ''}`}>
        <TableCell sx={{ verticalAlign: 'top', minWidth: 140 }}><Typography variant="body2" fontWeight={600}>{row.target}</Typography><Box sx={{ display: 'flex', flexWrap: 'wrap' }}>{evidence(row.evidence.filter(usableClauseEvidence))}</Box></TableCell>
        {kind !== 'other_kcr' && <><TableCell sx={{ verticalAlign: 'top' }}>{row.code || '—'}</TableCell><TableCell sx={{ verticalAlign: 'top' }} title={row.mapping_provenance || undefined}>{row.kcr_labels.join(' / ')}</TableCell></>}
        <TableCell sx={{ verticalAlign: 'top', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>{row.description || '—'}</TableCell>
      </TableRow>)}{tableRows(kind).length === 0 && <TableRow><TableCell colSpan={kind === 'other_kcr' ? 2 : 4}>No located entries.</TableCell></TableRow>}</TableBody>
    </Table></TableContainer>
  </Box>;
  return <Box sx={{
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    px: {
      xs: MOBILE_DENSITY.pageInset,
      md: 2
    },
    py: {
      xs: 2,
      md: 2
    }
  }}><Stack spacing={2}>
 <PageHeader eyebrow="DCMA" title={workflowName || 'Contract Receipt and Review'} subtitle="Fill the checklist from contract evidence and download a draft for review." />
 {readOnly && <Chip size="small" label="View only" sx={{ alignSelf: 'flex-start' }} />}
 {error && <Alert severity="error" action={<Button color="inherit" onClick={() => void load()}>Reload</Button>}>{error}</Alert>}
 {!state && !error && <LinearProgress aria-label="Loading saved review" />}
 <Paper data-testid="crr-upload-frame" variant="outlined" sx={{ p: { xs: 2, md: 3 } }}><Stack spacing={2}>
 <Typography component="h2" variant="h5">Upload contract</Typography>
 <Box><Grid container spacing={2} aria-label="Contract documents">
   <Grid item xs={12} md={6} sx={{ minWidth: 0 }}><CrrUploadTile title="Contract PDF" required
     label="Upload contract PDF" inputLabel="Choose contract PDF"
     selected={contract ? [contract] : []} saved={(state?.sources || []).filter(source => source.role === 'contract')}
     onFiles={selectContract} onRemove={() => setContract(null)} onDeleteSaved={removeSavedSource} uploading={uploading} disabled={locked} testId="crr-contract-upload-card" /></Grid>
   <Grid item xs={12} md={6} sx={{ minWidth: 0 }}><CrrUploadTile title="Supporting PDFs"
     label="Upload supporting files" inputLabel="Choose supporting files" multiple
     selected={support} saved={(state?.sources || []).filter(source => source.role === 'supporting')}
     onFiles={selectSupport} onRemove={index => setSupport(files => files.filter((_, i) => i !== index))} onDeleteSaved={removeSavedSource} uploading={uploading && support.length > 0} disabled={locked} testId="crr-support-upload-card" /></Grid>
 </Grid></Box>
 <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" justifyContent="center"><TextField label="Expected administering DoDAAC (optional)" size="small" value={expectedAdmin} disabled={!state || locked} onChange={event => {
   if (!permission.current && !locked) { contextTouched.current = true; setUseProfileDefault(false); setExpectedAdmin(event.target.value.toUpperCase()); setUseSavedWork(false); setError(''); }
 }} inputProps={{ maxLength: 6, autoCapitalize: 'characters', spellCheck: false }} sx={{ width: 320, maxWidth: '100%' }} />
 <Button size="small" disabled={!profile || locked} onClick={() => { contextTouched.current = true; setExpectedAdmin(profile?.admin_dodaac || ''); setUseProfileDefault(true); setUseSavedWork(false); }}>Use my default</Button>
 <Button size="small" disabled={readOnly || profileBusy} onClick={() => { setProfileDraft(profile?.admin_dodaac || ''); setProfileOpen(true); }}>Unit default</Button></Stack>
 <Stack alignItems="center" spacing={0}><Button variant="contained" disabled={(!contract && !savedPrimary) || !state || locked} onClick={submit}>Check contract</Button>
 <FormControlLabel sx={{ m: 0 }} control={<Checkbox size="small" checked={useSavedWork} disabled={locked || !hasSavedWork} onChange={event => { if (!permission.current && !locked && hasSavedWork) setUseSavedWork(event.target.checked); }} />} label="Use saved work" /></Stack>
 </Stack></Paper>
 {active && <Paper variant="outlined" sx={{
        p: 2
      }}><Typography role="status">{state?.run?.status_message || 'Review queued'}</Typography><StepProgressSpinner steps={STEPS} messages={state?.run?.messages || []} /><Button disabled={busy || readOnly} onClick={() => { const id = epoch.current; void action(async () => {
          if (state?.run) {
            await cancelDcmaCrrRun(flowId, state.run.job_id);
            if (id === epoch.current) await load();
          }
        }); }}>Cancel run</Button></Paper>}
 {state?.run && !active && state.run.status !== 'completed' && <Alert severity="warning">{state.run.error?.message || `Review ${state.run.status}.`}<Button disabled={readOnly || busy || (!contract && support.length > 0 && !savedPrimary)} onClick={() => {
          if (contract || support.length) { submit(); return; }
          if (!validAdminContext()) return;
          const id = epoch.current;
          void action(async () => {
          const next = await startDcmaCrrRun(flowId, state?.revision || 0, expectedAdminValue, ...selectedDefault);
          if (id === epoch.current) setState(next);
        }); }}>Retry review</Button></Alert>}
 {review && state?.run?.status === 'completed' && <Stack spacing={2}>
 <Typography component="h2" variant="h5">{review.status === 'partial' ? 'Draft ready — some evidence is incomplete' : 'Checklist ready'}</Typography>
 <Paper component="section" aria-labelledby="crr-clauses-heading" variant="outlined" sx={{ overflow: 'hidden' }}>
 <Box sx={{ px: 2, py: 1.25, bgcolor: 'background.default' }}><Typography id="crr-clauses-heading" component="h2" variant="h6">Clauses</Typography><Typography variant="body2" color="text.secondary">{locatedClauses.length} located checks</Typography></Box>
 {clauseTable('spc', 'SPC-PVN-CD')}{clauseTable('r9', 'R9 Codes')}{clauseTable('other_kcr', 'Other KCRs')}
 {locatedClauses.some(row => !mappingKind(row)) && <Box component="section" aria-label="Unmapped evidence" sx={{ p: 2 }}><Typography component="h3" variant="subtitle1" fontWeight={600}>Unmapped evidence</Typography>{locatedClauses.filter(row => !mappingKind(row)).map(row => <Box key={row.item_id} sx={{ py: 0.5 }}><Typography variant="body2">{row.target}</Typography>{evidence(row.evidence.filter(usableClauseEvidence))}</Box>)}</Box>}
 {(review.required_family_results || []).length > 0 && <Box component="section" aria-label="Required clause checks" sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
 <Typography component="h3" variant="subtitle1" fontWeight={600}>Required clause checks</Typography>
 {(review.required_family_results || []).map(family => {
   const legacyWawf = family.family_id === 'wawf' && family.label === 'WAWF / electronic submission';
   const has7006 = family.clause_ids.includes('252.232-7006');
   // Older saved reviews grouped 7003 and 7006 together; preserve those records,
   // but never display 7003 as proof that 7006 is present.
   const needsFreshCheck = legacyWawf && family.status === 'located' && !has7006;
   const status = needsFreshCheck ? 'unknown' : family.status;
   const label = legacyWawf ? 'WAWF payment instructions (252.232-7006)' : family.label;
   const ids = legacyWawf ? family.clause_ids.filter(id => id === '252.232-7006') : family.clause_ids;
   const proof = legacyWawf ? family.evidence.filter(item => has7006 && /252\.232-7006\b/.test(item.excerpt)) : family.evidence;
   return <Box key={family.family_id} sx={{ py: 0.75 }}><Typography variant="body2"><strong>{label}</strong>: {status === 'located' ? 'Located' : status === 'not_located' ? 'Not located' : 'Not verifiable'}{ids.length > 0 ? ` · ${ids.join(', ')}` : ''}</Typography>{needsFreshCheck && <Typography variant="body2">Run a fresh check to verify 252.232-7006. The saved spreadsheet reflects the original review.</Typography>}<Box sx={{ display: 'flex', flexWrap: 'wrap' }}>{evidence(proof.filter(usableClauseEvidence))}</Box></Box>;
 })}
 </Box>}
 </Paper>
 {(review.structure_results.length > 0 || review.guidance.length > 0) && <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}>
 <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="crr-checklist-content" id="crr-checklist-toggle"><Box><Typography component="h2" variant="h6">Contract Structure</Typography><Typography variant="body2" color="text.secondary">Contract details and initial screening. C = document supported; X = needs review; NA = not applicable.</Typography></Box></AccordionSummary>
 <AccordionDetails id="crr-checklist-content" sx={{ p: 0 }}>
 {review.structure_results.length > 0 && <Box component="section" aria-label="Contract Structure">
 {review.structure_results.filter(row => row.display_visible !== false && row.row !== 14).map(row => {
   const result = row.display_result || row.result;
   const conflict = row.reason_kind === 'document_inconsistency';
   const status = result === 'C' ? 'Document supported' : result === 'NA' ? 'Not applicable' : conflict ? 'Conflicting evidence' : 'Needs review';
   return <Box key={row.item_id} data-display-tone={row.display_tone || 'normal'} sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 2fr) minmax(0, 3fr)' }, gap: { xs: 1, md: 2 }, px: { xs: 1.5, md: 2 }, py: 1.25, borderTop: 1, borderColor: 'divider', ...(row.display_tone === 'muted' ? { bgcolor: 'action.hover', color: 'text.secondary' } : {}) }}>
   <Stack spacing={0.5} alignItems="flex-start"><Typography component="h3" variant="subtitle1" fontWeight={600}>{row.display_label || row.question}</Typography><Chip size="small" label={`${result} · ${status}`} color={row.display_tone === 'muted' ? 'default' : result === 'C' ? 'success' : result === 'NA' ? 'default' : conflict ? 'error' : 'warning'} /></Stack>
   <Stack spacing={0.5} sx={{ minWidth: 0 }}><Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.5 }}>{row.display_reason || row.information_found}</Typography>{row.evidence.length > 0 && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>{evidence(row.evidence)}</Box>}</Stack>
   </Box>;
 })}</Box>}
 {review.guidance.length > 0 && <Box component="section" aria-label="Guidance">
 <Box sx={{ px: 2, py: 1.25 }}><Typography component="h3" variant="h6">Guidance</Typography><Typography variant="body2" color="text.secondary">Initial checklist screening questions.</Typography></Box>
 {review.guidance.map(row => <Box key={row.item_id} sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 2fr) minmax(0, 3fr)' }, gap: 1, px: 2, py: 1.25, borderTop: 1, borderColor: 'divider' }}><Typography fontWeight={600}>{row.question}</Typography><Stack spacing={0.5}><Typography sx={{ textTransform: 'capitalize' }}>{row.answer}{row.answer.toLowerCase() === 'yes' && row.matched_codes?.length ? `: ${row.matched_codes.join(', ')}` : ''}</Typography>{!!row.unresolved_codes?.length && <Typography variant="body2">Needs review: {row.unresolved_codes.join(', ')}</Typography>}<Box sx={{ display: 'flex', flexWrap: 'wrap' }}>{evidence(row.evidence)}</Box></Stack></Box>)}
 </Box>}
 {review.sam_comparison && <Box component="section" aria-label="SAM contractor comparison" sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
 <Typography component="h3" variant="h6">SAM contractor comparison</Typography><Typography variant="body2">{({ matched: 'Matched', field_mismatch: 'Field mismatch', not_found: 'Not found', ambiguous: 'Ambiguous', unavailable: 'Unavailable', not_checked: 'Not checked' })[review.sam_comparison.status]}</Typography>
 <Typography variant="body2">{review.sam_comparison.reason}</Typography>
 <Grid container spacing={2} sx={{ mt: 0 }}><Grid item xs={12} md={6}><Typography variant="subtitle2">Contract</Typography><Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{[review.sam_comparison.contract.name, review.sam_comparison.contract.cage, review.sam_comparison.contract.uei, review.sam_comparison.contract.address].filter(Boolean).join(' · ')}</Typography></Grid>
 <Grid item xs={12} md={6}><Typography variant="subtitle2">SAM</Typography><Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{review.sam_comparison.entity ? [review.sam_comparison.entity.legal_name, review.sam_comparison.entity.dba_name ? `DBA: ${review.sam_comparison.entity.dba_name}` : null, review.sam_comparison.entity.cage, review.sam_comparison.entity.uei, review.sam_comparison.entity.physical_address, review.sam_comparison.entity.registration_status].filter(Boolean).join(' · ') : review.sam_comparison.status === 'unavailable' ? 'SAM data unavailable.' : review.sam_comparison.status === 'not_found' ? 'No matching registration found.' : 'No registration selected.'}</Typography></Grid></Grid>
 {review.sam_comparison.mismatches.length > 0 && <Typography variant="body2">Different fields: {review.sam_comparison.mismatches.join(', ')}</Typography>}
 <Stack direction="row" spacing={1} alignItems="center"><Button size="small" href="https://sam.gov" target="_blank" rel="noopener noreferrer">SAM.gov</Button>{review.sam_comparison.api_version && <Typography variant="caption" color="text.secondary">API {review.sam_comparison.api_version}</Typography>}{review.sam_comparison.retrieved_at && <Typography variant="caption" color="text.secondary">Retrieved {review.sam_comparison.retrieved_at}</Typography>}</Stack>
 </Box>}
 </AccordionDetails></Accordion>}
 </Stack>}
 {(state?.runs || []).filter(run => run.status === 'completed' && run.job_id !== state?.run?.job_id).map(run => <Paper variant="outlined" key={run.job_id} sx={{
        p: 2
      }}><Stack direction="row" spacing={2} alignItems="center"><Typography>Previous run — revision {run.generation}</Typography><OneTimeDownloadButton disabled={busy} onClick={() => download(run.job_id)}>Download previous checklist</OneTimeDownloadButton></Stack></Paper>)}
 {state?.run?.status === 'completed' && !contextChanged && !pendingInputs && <Box component="section" aria-label="Spreadsheet export" sx={{ display: 'flex', justifyContent: 'flex-end', pt: 3, borderTop: 1, borderColor: 'divider' }}><OneTimeDownloadButton variant="contained" color="primary" size="large" loading={busy} disabled={busy} onClick={() => download()} sx={{ width: { xs: '100%', sm: 'auto' } }}>Export spreadsheet</OneTimeDownloadButton></Box>}
 </Stack><Dialog open={profileOpen} onClose={() => { if (!profileBusy) setProfileOpen(false); }} aria-labelledby="crr-profile-title" fullWidth maxWidth="xs">
 <DialogTitle id="crr-profile-title">Unit default</DialogTitle><DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
 {profileError && <Alert severity="error">{profileError}</Alert>}
 <TextField label="Your administering DoDAAC" value={profileDraft} size="small" disabled={profileBusy || readOnly || !profile} onChange={event => setProfileDraft(event.target.value.toUpperCase())} inputProps={{ maxLength: 6, autoCapitalize: 'characters', spellCheck: false }} />
 <Stack direction="row" justifyContent="flex-end" spacing={1}><Button disabled={profileBusy} onClick={() => setProfileOpen(false)}>Cancel</Button><Button variant="contained" disabled={!profile || profileBusy || readOnly} onClick={() => void saveProfile()}>Save default</Button></Stack>
 </Stack></DialogContent></Dialog><Dialog open={!!sourceView} onClose={() => setSourceView(null)} fullWidth maxWidth={false} aria-labelledby="crr-evidence-title" sx={theme => ({
    zIndex: GLOBAL_NAVBAR_Z_INDEX + 1,
    top: GLOBAL_NAVBAR_HEIGHT,
    '& .MuiBackdrop-root': { top: GLOBAL_NAVBAR_HEIGHT },
    '& .MuiDialog-container': { alignItems: 'flex-start' },
    '& .MuiDialog-paper': {
      m: { xs: 1, sm: 2 }, minHeight: 0, maxWidth: 'none',
      width: { xs: `calc(100% - ${theme.spacing(2)})`, sm: `calc(100% - ${theme.spacing(4)})` },
      height: { xs: `calc(100dvh - ${GLOBAL_NAVBAR_HEIGHT.xs}px - ${theme.spacing(2)})`, sm: `calc(100dvh - ${GLOBAL_NAVBAR_HEIGHT.sm}px - ${theme.spacing(4)})` },
      maxHeight: { xs: `calc(100dvh - ${GLOBAL_NAVBAR_HEIGHT.xs}px - ${theme.spacing(2)})`, sm: `calc(100dvh - ${GLOBAL_NAVBAR_HEIGHT.sm}px - ${theme.spacing(4)})` },
    },
  })}><DialogTitle id="crr-evidence-title" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, flexWrap: 'nowrap', py: 0.5, px: { xs: 1, sm: 2 } }}>
    <Typography component="span" variant="body2" fontWeight={600} noWrap title={sourceView?.filename} sx={{ flex: 1, minWidth: 0 }}>{sourceView?.filename}</Typography>
    <Typography component="span" variant="body2" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>Page {sourceView?.page}</Typography>
    <Button size="small" sx={{ flexShrink: 0, minWidth: 0, minHeight: { xs: MOBILE_DENSITY.touchTarget, sm: 32 } }} onClick={() => setSourceView(null)}>Close</Button>
  </DialogTitle><DialogContent dividers sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr) minmax(0, 2fr)', md: 'minmax(0, 1fr) minmax(0, 3fr)' }, p: 0, minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
    <Box component="section" aria-label="Evidence excerpts" tabIndex={0} sx={{ minHeight: 0, minWidth: 0, overflowY: 'auto', p: { xs: 1, sm: 2 }, borderRight: 1, borderColor: 'divider' }}>{sourceView && <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.5 }}>{sourceView.excerpt}</Typography>}</Box>
    <Box component="section" aria-label="Source document" sx={{ minHeight: 0, minWidth: 0, overflow: 'hidden' }}>{sourceView && <Box component="iframe" title={sourceView.title} src={`${sourceView.url}#page=${sourceView.page}`} sx={{ display: 'block', width: '100%', height: '100%', border: 0 }} />}</Box>
  </DialogContent></Dialog></Box>;
}
